import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Truck, Package, Globe, MapPin, Clock, Shield, Search, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PageSeo } from '@/components/seo/PageSeo';

// Turkey Cargo Companies
const turkeyCargoCompanies = [
  { name: 'PTT Kargo', color: '#FFD100', textColor: '#000', abbreviation: 'PTT', trackUrl: 'https://gonderitakip.ptt.gov.tr' },
  { name: 'Aras Kargo', color: '#E31E24', textColor: '#FFF', abbreviation: 'ARAS', trackUrl: 'https://www.araskargo.com.tr/trs/kargo-takip' },
  { name: 'MNG Kargo', color: '#FF6B00', textColor: '#FFF', abbreviation: 'MNG', trackUrl: 'https://www.mngkargo.com.tr/gonderi-takip' },
  { name: 'Yurtiçi Kargo', color: '#00529B', textColor: '#FFF', abbreviation: 'YURTİÇİ', trackUrl: 'https://www.yurticikargo.com/tr/online-islemler/gonderi-sorgula' },
  { name: 'Sürat Kargo', color: '#E4002B', textColor: '#FFF', abbreviation: 'SÜRAT', trackUrl: 'https://www.suratkargo.com.tr/gonderi-takip' },
  { name: 'Horoz Lojistik', color: '#1B4F72', textColor: '#FFF', abbreviation: 'HOROZ', trackUrl: 'https://www.horozlojistik.com.tr' },
  { name: 'UPS Türkiye', color: '#351C15', textColor: '#FFB500', abbreviation: 'UPS', trackUrl: 'https://www.ups.com/track' },
];

// International Cargo Companies with country keys for translation
const internationalCargoCompanies = [
  { name: 'UPS', countryKey: 'usa', color: '#351C15', textColor: '#FFB500', abbreviation: 'UPS', trackUrl: 'https://www.ups.com/track' },
  { name: 'FedEx', countryKey: 'usa', color: '#4D148C', textColor: '#FF6200', abbreviation: 'FedEx', trackUrl: 'https://www.fedex.com/fedextrack' },
  { name: 'DHL', countryKey: 'germany', color: '#FFCC00', textColor: '#D40511', abbreviation: 'DHL', trackUrl: 'https://www.dhl.com/tr-tr/home/tracking.html' },
  { name: 'Maersk', countryKey: 'denmark', color: '#00243D', textColor: '#42B4E6', abbreviation: 'MAERSK', trackUrl: 'https://www.maersk.com/tracking' },
  { name: 'SF Express', countryKey: 'china', color: '#000', textColor: '#E31937', abbreviation: 'SF', trackUrl: 'https://www.sf-express.com/tr/en/dynamic_function/waybill' },
  { name: 'China Post', countryKey: 'china', color: '#006633', textColor: '#FFF', abbreviation: 'CHINA POST', trackUrl: 'https://track.yw56.com.cn' },
  { name: 'USPS', countryKey: 'usa', color: '#004B87', textColor: '#FFF', abbreviation: 'USPS', trackUrl: 'https://tools.usps.com/go/TrackConfirmAction' },
  { name: 'Deutsche Post', countryKey: 'germany', color: '#FFCC00', textColor: '#000', abbreviation: 'DP', trackUrl: 'https://www.deutschepost.de/sendung/simpleQuery.html' },
  { name: 'DB Schenker', countryKey: 'germany', color: '#E30613', textColor: '#FFF', abbreviation: 'DB', trackUrl: 'https://www.dbschenker.com/tracking' },
  { name: 'La Poste', countryKey: 'france', color: '#003DA5', textColor: '#FFD200', abbreviation: 'LA POSTE', trackUrl: 'https://www.laposte.fr/outils/suivre-vos-envois' },
  { name: 'Japan Post', countryKey: 'japan', color: '#E60012', textColor: '#FFF', abbreviation: 'JP', trackUrl: 'https://www.post.japanpost.jp/int/ems/delivery/index_en.html' },
  { name: 'JR Freight', countryKey: 'japan', color: '#0068B7', textColor: '#FFF', abbreviation: 'JR', trackUrl: 'https://www.jrfreight.co.jp' },
  { name: 'Emirates Post', countryKey: 'uae', color: '#C8102E', textColor: '#FFF', abbreviation: 'EP', trackUrl: 'https://emiratespost.ae/track' },
  { name: 'Canadian National Railway', countryKey: 'canada', color: '#E31837', textColor: '#FFF', abbreviation: 'CN', trackUrl: 'https://www.cn.ca' },
  { name: 'CP Rail', countryKey: 'canada', color: '#E31837', textColor: '#FFF', abbreviation: 'CP', trackUrl: 'https://www.cpr.ca' },
  { name: 'CSX', countryKey: 'usa', color: '#004990', textColor: '#FFF', abbreviation: 'CSX', trackUrl: 'https://www.csx.com' },
  { name: 'Union Pacific', countryKey: 'usa', color: '#FFD100', textColor: '#00205B', abbreviation: 'UP', trackUrl: 'https://www.up.com' },
  { name: 'BNSF Railway', countryKey: 'usa', color: '#FF6600', textColor: '#003300', abbreviation: 'BNSF', trackUrl: 'https://www.bnsf.com' },
  { name: 'Norfolk Southern', countryKey: 'usa', color: '#000', textColor: '#FFF', abbreviation: 'NS', trackUrl: 'https://www.nscorp.com' },
  { name: 'McLane', countryKey: 'usa', color: '#003366', textColor: '#FFF', abbreviation: 'McLane', trackUrl: 'https://www.mclaneco.com' },
  { name: 'Kuehne + Nagel', countryKey: 'switzerland', color: '#003A70', textColor: '#FFF', abbreviation: 'K+N', trackUrl: 'https://www.kuehne-nagel.com' },
  { name: 'DSV', countryKey: 'denmark', color: '#E30613', textColor: '#FFF', abbreviation: 'DSV', trackUrl: 'https://www.dsv.com/en/support/track-and-trace' },
  { name: 'Yunda Express', countryKey: 'china', color: '#E31E24', textColor: '#FFF', abbreviation: 'YUNDA', trackUrl: 'https://www.yundaex.com' },
  { name: 'Russian Railways', countryKey: 'russia', color: '#E31E24', textColor: '#FFF', abbreviation: 'RZD', trackUrl: 'https://www.rzd.ru' },
  { name: 'MTR Corporation', countryKey: 'china', color: '#AC2E45', textColor: '#FFF', abbreviation: 'MTR', trackUrl: 'https://www.mtr.com.hk' },
  { name: 'Uber Freight', countryKey: 'usa', color: '#000', textColor: '#FFF', abbreviation: 'UBER', trackUrl: 'https://www.uber.com/freight' },
];

// All carriers for tracking dropdown
const allCarriers = [
  ...turkeyCargoCompanies.map(c => ({ ...c, group: 'turkey' })),
  ...internationalCargoCompanies.map(c => ({ ...c, group: 'international' })),
];

// Cargo Card Component
const CargoCard = ({ company, showCountry = false, countryText }) => (
  <div 
    className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
    data-testid={`cargo-card-${company.name.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div 
      className="w-14 h-14 rounded-lg flex items-center justify-center text-xs font-bold shrink-0"
      style={{ 
        backgroundColor: company.color,
        color: company.textColor
      }}
    >
      {company.abbreviation}
    </div>
    <div className="min-w-0">
      <h4 className="font-semibold text-gray-900 text-sm truncate">{company.name}</h4>
      {showCountry && countryText && (
        <p className="text-xs text-gray-500 flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          {countryText}
        </p>
      )}
    </div>
  </div>
);

const ShippingPage = () => {
  const { t } = useTranslation();
  const [trackingNumber, setTrackingNumber] = useState('');
  const [selectedCarrier, setSelectedCarrier] = useState('');
  const [trackingResult, setTrackingResult] = useState(null);
  const [isTracking, setIsTracking] = useState(false);

  const handleTrack = () => {
    if (!trackingNumber.trim() || !selectedCarrier) return;
    
    setIsTracking(true);
    
    // Simulate tracking lookup
    setTimeout(() => {
      // Find the carrier and open their tracking page
      const carrier = allCarriers.find(c => c.name === selectedCarrier);
      if (carrier && carrier.trackUrl) {
        // Open carrier's tracking page in new tab
        window.open(carrier.trackUrl, '_blank');
        setTrackingResult({
          success: true,
          message: t('shipping.trackingSuccess', { date: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString() })
        });
      } else {
        setTrackingResult({
          success: false,
          message: t('shipping.trackingNotFound')
        });
      }
      setIsTracking(false);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="shipping-page">
      <PageSeo title={t('shipping.pageTitle')} description={t('shipping.heroDescription')} segments={['shipping']} />
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-primary transition-colors">
              {t('common.home')}
            </Link>
            <span>/</span>
            <span className="text-gray-900 font-medium">{t('shipping.pageTitle')}</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary to-primary/80 text-white py-12">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl md:text-4xl font-bold mb-4 flex items-center gap-3">
              <Truck className="w-10 h-10" />
              {t('shipping.pageTitle')}
            </h1>
            <p className="text-lg text-white/90">
              {t('shipping.heroDescription')}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 md:px-8 py-12">
        
        {/* Tracking Section */}
        <section className="mb-12 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <Search className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900">{t('shipping.trackingTitle')}</h2>
              <p className="text-sm text-gray-600">{t('shipping.trackingDescription')}</p>
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Input
                type="text"
                placeholder={t('shipping.trackingPlaceholder')}
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                className="h-12"
                data-testid="tracking-number-input"
              />
            </div>
            <div className="w-full md:w-64">
              <Select value={selectedCarrier} onValueChange={setSelectedCarrier}>
                <SelectTrigger className="h-12" data-testid="carrier-select">
                  <SelectValue placeholder={t('shipping.selectCarrier')} />
                </SelectTrigger>
                <SelectContent>
                  <div className="px-2 py-1.5 text-xs font-semibold text-gray-500">{t('shipping.turkeyCarriers')}</div>
                  {turkeyCargoCompanies.map((carrier) => (
                    <SelectItem key={carrier.name} value={carrier.name}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-5 h-5 rounded text-[8px] font-bold flex items-center justify-center"
                          style={{ backgroundColor: carrier.color, color: carrier.textColor }}
                        >
                          {carrier.abbreviation.substring(0, 2)}
                        </div>
                        {carrier.name}
                      </div>
                    </SelectItem>
                  ))}
                  <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 border-t mt-1">{t('shipping.internationalCarriers')}</div>
                  {internationalCargoCompanies.slice(0, 10).map((carrier) => (
                    <SelectItem key={carrier.name} value={carrier.name}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-5 h-5 rounded text-[8px] font-bold flex items-center justify-center"
                          style={{ backgroundColor: carrier.color, color: carrier.textColor }}
                        >
                          {carrier.abbreviation.substring(0, 2)}
                        </div>
                        {carrier.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button 
              onClick={handleTrack} 
              disabled={!trackingNumber.trim() || !selectedCarrier || isTracking}
              className="h-12 px-8"
              data-testid="track-button"
            >
              {isTracking ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Search className="w-4 h-4 mr-2" />
              )}
              {t('shipping.trackButton')}
            </Button>
          </div>

          {/* Tracking Result */}
          {trackingResult && (
            <div className={`mt-4 p-4 rounded-lg flex items-center gap-3 ${
              trackingResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
            }`} data-testid="tracking-result">
              {trackingResult.success ? (
                <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
              )}
              <p className={`text-sm ${trackingResult.success ? 'text-green-800' : 'text-red-800'}`}>
                {trackingResult.message}
              </p>
            </div>
          )}
        </section>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
              <Clock className="w-6 h-6 text-primary" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">{t('shipping.fastDelivery')}</h3>
            <p className="text-sm text-gray-600">{t('shipping.fastDeliveryDesc')}</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <Shield className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">{t('shipping.securePackaging')}</h3>
            <p className="text-sm text-gray-600">{t('shipping.securePackagingDesc')}</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <Globe className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">{t('shipping.worldwideDelivery')}</h3>
            <p className="text-sm text-gray-600">{t('shipping.worldwideDeliveryDesc')}</p>
          </div>
        </div>

        {/* Turkey Cargo Companies */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
              <span className="text-white text-xl">🇹🇷</span>
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900">{t('shipping.turkeyCarriers')}</h2>
              <p className="text-sm text-gray-600">{t('shipping.turkeyCarriersDesc')}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {turkeyCargoCompanies.map((company) => (
              <CargoCard key={company.name} company={company} />
            ))}
          </div>
        </section>

        {/* International Cargo Companies */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Globe className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900">{t('shipping.internationalCarriers')}</h2>
              <p className="text-sm text-gray-600">{t('shipping.internationalCarriersDesc')}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {internationalCargoCompanies.map((company) => (
              <CargoCard 
                key={company.name} 
                company={company} 
                showCountry 
                countryText={t(`shipping.countries.${company.countryKey}`)}
              />
            ))}
          </div>
        </section>

        {/* Info Box */}
        <div className="mt-12 bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <Package className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-900 mb-2">{t('shipping.infoTitle')}</h3>
              <p className="text-sm text-amber-800">
                {t('shipping.infoDescription')} {' '}
                <Link to="/contact" className="text-primary font-semibold hover:underline">
                  {t('shipping.contactLink')}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShippingPage;
